/**
 * Tiny predicate evaluator shared across g-kpi, g-chart, g-card-grid.
 *
 * Spec shape (recursive):
 *   { eq: value }                                  // strict equality
 *   { ne: value }
 *   { in: [v1, v2, ...] }
 *   { nin: [v1, v2, ...] }                         // not in
 *   { gt: value } | { gte: value }
 *   { lt: value } | { lte: value }
 *   { between: [min, max] }                        // inclusive both ends
 *   { contains: substring }                        // string includes (case-insensitive)
 *   { exists: true|false }
 *   A date operand may be relative: "$today", "$today+7d", "$today-30d".
 *   { and: [pred, pred, ...] }
 *   { or: [pred, pred, ...] }
 *   { not: pred }
 *
 * For predicates against a record (with multiple fields), use — field names
 * may be dotted paths into a populated ref (`status.name`):
 *   { field: 'Status', op: { eq: 'Active' } }
 *   { all: { Status: { eq: 'Active' }, Score: { gt: 80 } } }
 *
 * Returns boolean. Unknown/invalid predicates return false (fail closed).
 */

import { getField } from './fields.js';

export function evalPredicate(value, pred) {
  if (pred == null || typeof pred !== 'object') return false;

  if ('eq' in pred) return same(value, pred.eq);
  if ('ne' in pred) return !same(value, pred.ne);
  if ('in' in pred) return Array.isArray(pred.in) && pred.in.some((x) => same(value, x));
  if ('nin' in pred) return Array.isArray(pred.nin) && !pred.nin.some((x) => same(value, x));
  if ('gt' in pred) return numCompare(value, pred.gt, (a, b) => a > b);
  if ('gte' in pred) return numCompare(value, pred.gte, (a, b) => a >= b);
  if ('lt' in pred) return numCompare(value, pred.lt, (a, b) => a < b);
  if ('lte' in pred) return numCompare(value, pred.lte, (a, b) => a <= b);
  if ('between' in pred && Array.isArray(pred.between) && pred.between.length === 2) {
    return numCompare(value, pred.between[0], (a, b) => a >= b)
        && numCompare(value, pred.between[1], (a, b) => a <= b);
  }
  if ('contains' in pred) {
    return value != null && String(value).toLowerCase().includes(String(pred.contains).toLowerCase());
  }
  if ('exists' in pred) {
    const has = value != null && value !== '';
    return pred.exists ? has : !has;
  }
  if ('and' in pred && Array.isArray(pred.and)) return pred.and.every(p => evalPredicate(value, p));
  if ('or' in pred && Array.isArray(pred.or)) return pred.or.some(p => evalPredicate(value, p));
  if ('not' in pred) return !evalPredicate(value, pred.not);

  return false;
}

// Evaluate a record-level predicate (works against an object with named fields).
//   evalRecord(rec, { field: 'Status', op: { eq: 'Active' } })
//   evalRecord(rec, { all: { Status: { eq: 'Active' }, Score: { gt: 80 } } })
export function evalRecord(record, pred) {
  if (!pred || typeof pred !== 'object') return false;
  if ('field' in pred && 'op' in pred) return evalPredicate(getField(record, pred.field), pred.op);
  if ('all' in pred && pred.all && typeof pred.all === 'object') {
    return Object.entries(pred.all).every(([f, op]) => evalPredicate(getField(record, f), op));
  }
  if ('any' in pred && pred.any && typeof pred.any === 'object') {
    return Object.entries(pred.any).some(([f, op]) => evalPredicate(getField(record, f), op));
  }
  if ('and' in pred && Array.isArray(pred.and)) return pred.and.every(p => evalRecord(record, p));
  if ('or' in pred && Array.isArray(pred.or)) return pred.or.some(p => evalRecord(record, p));
  return false;
}

/**
 * A relative date — "$today", "$today+7d", "$today-30d" (UTC days, the storage
 * convention for date-only fields) — as an ISO instant; anything else as-is.
 * The platform's dashboard and server evaluators resolve the same tokens.
 */
export function resolveRelativeDate(v) {
  const m = typeof v === 'string' ? /^\$today(?:([+-])(\d+)d)?$/.exec(v.trim()) : null;
  if (!m) return v;
  const now = new Date();
  const base = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return new Date(base + (m[2] ? (m[1] === '-' ? -1 : 1) * Number(m[2]) * 86400000 : 0)).toISOString();
}

// Equality; two dates are equal when they are the same instant.
function same(value, target) {
  const t = resolveRelativeDate(target);
  if (looksLikeDate(value) && looksLikeDate(t)) return toTime(value) === toTime(t);
  return value === t;
}

function numCompare(value, target, cmp) {
  // If target is { field: 'X', value: N } we ignore the field part here
  // (record-level evaluator handles cross-field comparisons).
  const t = resolveRelativeDate(typeof target === 'object' && !(target instanceof Date) && target !== null
    ? target.value
    : target);
  // Prefer date comparison when either side is a Date instance or looks like
  // an ISO-8601 string. `parseFloat('2025-04-16')` returns 2025, which would
  // otherwise mask dates as numbers — detect date-shape first.
  if (looksLikeDate(value) || looksLikeDate(t)) {
    const da = toTime(value);
    const db = toTime(t);
    if (da != null && db != null) return cmp(da, db);
    return false;
  }
  const a = parseFloat(value);
  const b = parseFloat(t);
  if (isNaN(a) || isNaN(b)) return false;
  return cmp(a, b);
}

function looksLikeDate(v) {
  if (v instanceof Date) return true;
  if (typeof v !== 'string') return false;
  // ISO 8601 date or date-time prefix (YYYY-MM-DD optionally followed by T…)
  return /^\d{4}-\d{2}-\d{2}(T|$)/.test(v);
}

function toTime(v) {
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  if (typeof v !== 'string' || !v) return null;
  const t = Date.parse(v);
  return isNaN(t) ? null : t;
}
